import type { ReactElement } from 'react';
import { MDText } from 'i18n-react';

import { InfoIcon } from '@motorway/mw-highway-code';

import {
	LoadingView,
	QueryViewStateHandler,
	SuccessView,
} from 'Components/QueryViewStateHandler/QueryViewStateHandler';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import Skeleton from 'Storybook/core/Skeleton/Skeleton';
import { Tooltip } from 'Storybook/core/Tooltip';
import { DATE_FORMATS, formatDate } from 'Utilities/formatters';
import type { UseInsightsRecentSoldResult } from 'Utilities/hooks/recentSoldPrices/useRecentSoldPrices';
import { useCypressData } from 'Utilities/hooks/useCypressData';

import LocalTexts from '../Title/Title.texts';

import styles from './DataLastUpdated.module.scss';

const LocalT = new MDText(LocalTexts);

type DataLastUpdatedProps = { insightsRecentSold: UseInsightsRecentSoldResult };

export const DataLastUpdated = ({ insightsRecentSold }: DataLastUpdatedProps): ReactElement => {
	const { data, isError, isLoading, isSuccess: isApiSuccess } = insightsRecentSold;
	const {
		minWidth: { breakpointTablet: isDesktop },
	} = useBreakpoint();
	const [cypressIds, applyCypressDataId] = useCypressData();

	const isEmpty = data && data.vehicles.length === 0;
	const isSuccess = isApiSuccess && !isEmpty;

	return (
		<QueryViewStateHandler isEmpty={isEmpty} isError={isError} isLoading={isLoading} isSuccess={isSuccess}>
			<SuccessView>
				<div
					className={styles.dataLastUpdated}
					data-testid="data-last-updated"
					{...applyCypressDataId(cypressIds.text.insights.results.dataLastUpdated)}
				>
					{isDesktop && (
						<Tooltip content={String(LocalT.translate('dataDisclaimer'))} sideOffset={0}>
							<div
								className={styles.iconContainer}
								data-testid="info-tooltip"
								tabIndex={0}
								{...applyCypressDataId(cypressIds.insights.recentSoldInfoTooltip)}
							>
								<InfoIcon filled />
							</div>
						</Tooltip>
					)}
					<span className={styles.text}>
						{data?.lastUpdatedAt
							? LocalT.translate('dateLastUpdated', {
									date: formatDate(data.lastUpdatedAt, DATE_FORMATS.DAY_MONTH_YEAR),
							  })
							: LocalT.translate('dataUpdatedDaily')}
					</span>
				</div>
			</SuccessView>
			<LoadingView>
				<div className={styles.text} data-testid="loading-blob">
					<Skeleton height={3} width={30} />
				</div>
			</LoadingView>
		</QueryViewStateHandler>
	);
};
